import React from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { tokens } from '../../theme';

export const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Date range + run/export controls shared by the vendor reports.
export const ReportFilters = ({
    startDate, endDate, onStartChange, onEndChange, onRun, onExport, running, canExport, children,
}) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                <DatePicker
                    label="From"
                    value={startDate}
                    onChange={onStartChange}
                    maxDate={endDate || undefined}
                    slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
                <DatePicker
                    label="To"
                    value={endDate}
                    onChange={onEndChange}
                    minDate={startDate || undefined}
                    slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
                {children}
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" onClick={onRun} disabled={running}>
                    {running ? 'Running…' : 'Run report'}
                </Button>
                <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport} disabled={!canExport}>
                    Export CSV
                </Button>
            </Box>
        </LocalizationProvider>
    </Paper>
);

export const ReportError = ({ message }) =>
    message ? (
        <Alert severity="error" sx={{ mb: 2 }}>
            {message}
        </Alert>
    ) : null;

// Loading, "run a report" and "no results" states for the results area.
export const ReportState = ({ loading, hasRun, empty, emptyMessage }) => {
    if (loading) {
        return (
            <Paper variant="outlined" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
            </Paper>
        );
    }
    if (!hasRun) {
        return (
            <Paper variant="outlined" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6" component="p">
                    Pick a date range and run the report
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Results show here and can be exported to CSV.
                </Typography>
            </Paper>
        );
    }
    if (empty) {
        return (
            <Paper variant="outlined" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6" component="p">
                    No results
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    {emptyMessage || 'Nothing found for this date range. Try a wider range.'}
                </Typography>
            </Paper>
        );
    }
    return null;
};

export { default as ReportPagination, pageSlice } from './AppPagination';

// Clickable summary tile that doubles as a filter.
export const CountTile = ({ label, count, active, onClick }) => (
    <Paper
        component="button"
        type="button"
        variant="outlined"
        onClick={onClick}
        aria-pressed={active}
        sx={{
            flex: '1 1 160px',
            textAlign: 'left',
            p: 2,
            cursor: 'pointer',
            font: 'inherit',
            borderColor: active ? tokens.blue : tokens.line,
            boxShadow: active ? `inset 0 0 0 1px ${tokens.blue}` : 'none',
            bgcolor: active ? '#EEF3FA' : 'background.paper',
            '&:hover': { borderColor: tokens.blue },
        }}
    >
        <Typography sx={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
            {count}
        </Typography>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
    </Paper>
);

export const downloadCsv = (csv, fileName) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
